import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import API, { formatApiErrorDetail } from '../../utils/axios';
import {
  LayoutDashboard, Trophy, Users, Target, Ticket,
  Calendar, Menu, X, LogOut, FileText, Save
} from 'lucide-react';

export const AdminTerms = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/admin/settings', { withCredentials: true });
        setContent(data?.terms_content || '');
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal memuat konten');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/admin/settings', { terms_content: content }, { withCredentials: true });
      toast.success('Syarat & Ketentuan berhasil disimpan');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Pertandingan', icon: Calendar, path: '/admin/matches' },
    { name: 'Tim', icon: Trophy, path: '/admin/teams' },
    { name: 'Prediksi', icon: Target, path: '/admin/predictions' },
    { name: 'Tiket', icon: Ticket, path: '/admin/tickets' },
    { name: 'Partisipan', icon: Users, path: '/admin/participants' },
    { name: 'Syarat & Ketentuan', icon: FileText, path: '/admin/terms' },
  ];

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-terms-page">
      <aside className={`fixed inset-y-0 left-0 w-64 bg-[rgba(255,255,255,0.03)] border-r border-white/10 transform transition-transform lg:translate-x-0 z-50 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D90429] to-[#B00320] rounded-xl flex items-center justify-center">
              <span className="text-white font-black text-2xl">ET</span>
            </div>
            <div>
              <div className="text-white font-black text-lg">EXTRA TIME</div>
              <div className="text-gray-400 text-xs">Admin Panel</div>
            </div>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  item.path === '/admin/terms'
                    ? 'bg-[#D90429] text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon size={20} />
                <span className="text-sm">{item.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D90429] rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{admin?.username?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm truncate">{admin?.display_name || admin?.username}</div>
              <div className="text-gray-400 text-xs">{admin?.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white/10 text-white"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Syarat &amp; Ketentuan</h1>
            <p className="text-gray-400">Edit konten halaman Syarat &amp; Ketentuan publik</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="save-terms-btn"
          >
            <Save size={18} />
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>

        <div className="glass-card p-6">
          <label className="block text-white font-semibold mb-3">
            Konten (mendukung Markdown sederhana: <code className="text-[#FFD60A]">## Judul</code>, <code className="text-[#FFD60A]">- item</code>)
          </label>
          {loading ? (
            <div className="text-gray-400">Loading...</div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={22}
              placeholder="Masukkan konten Syarat & Ketentuan di sini..."
              className="w-full font-mono text-sm"
              data-testid="terms-content-input"
            />
          )}
          <p className="text-gray-400 text-xs mt-3">
            Tip: Pisahkan bagian dengan baris kosong. Gunakan <code>## Judul</code> untuk sub-judul dan <code>- item</code> untuk daftar.
          </p>
        </div>
      </main>
    </div>
  );
};
